// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { SearchParams } from '@api/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button } from '@imprivata-cloud/components';
import {
  Button as ButtonDS,
  type DateRange,
  DateRangePicker,
  Select,
} from '@imprivata-cloud/design-system';
import { type RouteArea, routeAreaOf } from '@portal/constants';
import {
  type ItemOwner,
  isReportKey,
  REPORT_ITEMS,
  type ReportItem,
  type ReportKey,
  useIsAvailable,
} from '@portal/containers/settings/SettingsRegistry';
import { useStatusAnnouncer } from '@portal/hooks/useStatusAnnouncer';
import { useMutation } from '@tanstack/react-query';
import { omitSearchParam } from '@utils/navigationSearch';
import { downloadAsFileToUserComputer, lastXDaysDateRange, MS_PER_DAY } from '@utils/utils';
import { message } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import DownloadImg from '../../../assets/icons/download.svg?react';
import SuccessIcon from '../../../assets/icons/success.svg?react';
import './ActivityReport.less';

const ROOT = 'portal.reports';
const ACTIVITY = 'portal.activity-report';
const MAX_DAYS = 30;
const SELECT_ID = 'report-select';

type Bounds = { from: Date; to: Date };
type Download = { fetch: () => Promise<Blob>; fileName: string };

const covers = (owner: ItemOwner, area: RouteArea | undefined) =>
  owner === 'both' || owner === area;

// Calendar days, counted inclusively, so a DST shift cannot push a 30-day pick over the limit.
const spanInDays = ({ from, to }: Bounds) =>
  Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      MS_PER_DAY,
  ) + 1;

// The picker caps a selection at MAX_DAYS; this keeps any other range from reaching the API.
const validBounds = (range: DateRange | undefined): Bounds | undefined => {
  if (!range?.from || !range?.to) {
    return undefined;
  }
  const bounds = { from: range.from, to: range.to };
  return spanInDays(bounds) > MAX_DAYS ? undefined : bounds;
};

const toDownload = (report: ReportItem, range: DateRange | undefined): Download | undefined => {
  if (report.range === 'none') {
    return { fetch: report.fetch, fileName: report.fileName };
  }
  const bounds = validBounds(range);
  return bounds && { fetch: () => report.fetch(bounds), fileName: report.fileName };
};

const showDownloadStarted = (
  messageApi: MessageInstance,
  t: TFunction,
  announce: (...lines: string[]) => void,
) => {
  const title = t('portal.notifications.download.title');
  const text = t('portal.notifications.download.text');
  void messageApi.success({
    duration: 10,
    className: 'download-started-banner',
    content: (
      <div className="banner-content">
        <div className="header">{title}</div>
        <div>{text}</div>
      </div>
    ),
    icon: <SuccessIcon />,
  });
  announce(title, text);
};

export const useReportDownload = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [messageApi, contextHolder] = message.useMessage();
  const [announce, statusRegion] = useStatusAnnouncer();

  const { mutate, isPending } = useMutation<Blob, ApiError, Download>({
    mutationFn: ({ fetch }) => fetch(),
    onSuccess: (data, { fileName }) => {
      showDownloadStarted(messageApi, t, announce);
      downloadAsFileToUserComputer(data, fileName);
    },
    onError: e => emitError(new AppError(e)),
  });

  const download = (report: ReportItem, range?: DateRange) => {
    const ready = toDownload(report, range);
    if (ready) {
      mutate(ready);
    }
  };

  return { download, isPending, contextHolder, statusRegion };
};

export default function Reports() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const available = useIsAvailable();
  const { download, isPending, contextHolder, statusRegion } = useReportDownload();
  const [selectedKey, setSelectedKey] = useState<ReportKey | null>(() => {
    const value = searchParams.get(SearchParams.REPORT);
    return isReportKey(value) ? value : null;
  });
  const [range, setRange] = useState<DateRange>();

  const area = routeAreaOf(pathname);
  const reports = REPORT_ITEMS.filter(
    report => covers(report.owner, area) && available(report.gate),
  );
  const selected = reports.find(report => report.key === selectedKey);
  const ready = selected && toDownload(selected, range);

  const handleSelect = (key: ReportKey | null) => {
    setSelectedKey(key);
    setRange(undefined);
    if (searchParams.has(SearchParams.REPORT)) {
      setSearchParams(omitSearchParam(searchParams, SearchParams.REPORT), { replace: true });
    }
  };

  return (
    <>
      {contextHolder}
      {statusRegion}
      <div className="activity-report reports-form">
        <p>{t(`${ROOT}.description`)}</p>
        <Select
          id={SELECT_ID}
          label={t(`${ROOT}.select-label`)}
          items={reports.map(report => ({ value: report.key, label: t(report.labelKey) }))}
          value={selected?.key ?? null}
          onSelectionChange={value => handleSelect(isReportKey(value) ? value : null)}
        />
        {selected?.range === 'dates' && (
          <DateRangePicker
            className="date-range-picker"
            label={t(`${ACTIVITY}.date-range-label`)}
            subLabel={t(`${ACTIVITY}.date-range-subscript`)}
            selected={range}
            onSelect={setRange}
            calendarProps={{ mode: 'range', disabled: { after: new Date() }, max: MAX_DAYS - 1 }}
            footerActions={
              <div className="date-range-footer">
                {[7, 14, MAX_DAYS].map(days => (
                  <ButtonDS
                    key={days}
                    variant="default"
                    size="sm"
                    onClick={() => setRange(lastXDaysDateRange(days))}
                  >
                    {t(`${ACTIVITY}.date-range-value`, { count: days })}
                  </ButtonDS>
                ))}
              </div>
            }
          />
        )}
      </div>
      <div className="activity-report reports-actions">
        <Button
          type="primary"
          disabled={!ready || isPending}
          size="large"
          icon={<DownloadImg />}
          onClick={() => selected && download(selected, range)}
        >
          {t(`${ACTIVITY}.download-button`)}
        </Button>
      </div>
    </>
  );
}
